import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import type { Node as ReactFlowNode } from 'reactflow'
import { type MentionCandidate, buildCandidates } from './mentionUtils'
import { useProjectId } from '../../contexts/ProjectIdContext'
import {
  createMentionChipElement,
  serializeNodeText,
  getPlainTextFromEditor,
  getReferencedNodeIdsFromEditor,
  selectionInsideEditor,
  placeCaretAtEnd,
  hydrateContent,
  MentionDropdownPortal,
} from './contentEditableUtils'

export interface InlineMentionComposerState {
  plainText: string
  referencedNodeIds: string[]
}

export interface InlineMentionComposerHandle {
  clear: () => void
  focus: () => void
  setText: (text: string) => void
  insertMentionTrigger: () => void
  getPlainText: () => string
  getReferencedNodeIds: () => string[]
}

interface InlineMentionComposerProps {
  canvasNodes?: ReactFlowNode[]
  disabled?: boolean
  placeholder?: string
  /** Raw text containing @[[nodeId]] markers to hydrate into chips on mount */
  initialContent?: string
  onContentChange?: (state: InlineMentionComposerState) => void
  onSubmit?: () => void
  onEscape?: () => void
  onFocusChange?: (isFocused: boolean) => void
  onMentionClick?: (nodeId: string) => void
}

export const InlineMentionComposer = forwardRef<
  InlineMentionComposerHandle,
  InlineMentionComposerProps
>(function InlineMentionComposer(
  {
    canvasNodes,
    disabled = false,
    placeholder = 'Type a message to start...',
    initialContent,
    onContentChange,
    onSubmit,
    onEscape,
    onFocusChange,
    onMentionClick,
  },
  ref,
) {
  const projectId = useProjectId()
  const editorRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<{ node: globalThis.Node; offset: number } | null>(null)
  const composingRef = useRef(false)

  const [showDropdown, setShowDropdown] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [highlightIndex, setHighlightIndex] = useState(0)

  const candidates = useMemo(() => (canvasNodes ? buildCandidates(canvasNodes) : []), [canvasNodes])
  const filteredCandidates = useMemo(() => {
    if (!filterText) return candidates
    const query = filterText.toLowerCase()
    return candidates.filter(
      (candidate) =>
        candidate.label.toLowerCase().includes(query) || candidate.type.toLowerCase().includes(query),
    )
  }, [candidates, filterText])

  const adjustHeight = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    editor.style.height = 'auto'
    const nextHeight = Math.min(Math.max(editor.scrollHeight, 24), 160)
    editor.style.height = `${nextHeight}px`
  }, [])

  const emitContentState = useCallback(() => {
    const editor = editorRef.current
    const state = {
      plainText: getPlainTextFromEditor(editor),
      referencedNodeIds: getReferencedNodeIdsFromEditor(editor),
    }
    onContentChange?.(state)
    return state
  }, [onContentChange])

  const closeDropdown = useCallback(() => {
    anchorRef.current = null
    setShowDropdown(false)
    setFilterText('')
    setHighlightIndex(0)
  }, [])

  const syncMentionTrigger = useCallback(() => {
    const editor = editorRef.current
    if (!editor || candidates.length === 0 || composingRef.current) {
      closeDropdown()
      return
    }

    const selection = window.getSelection()
    if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
      closeDropdown()
      return
    }

    const range = selection.getRangeAt(0)
    const textNode = range.startContainer
    if (textNode.nodeType !== Node.TEXT_NODE) {
      closeDropdown()
      return
    }

    const text = textNode.textContent ?? ''
    const cursorPos = range.startOffset

    let atPos = -1
    for (let i = cursorPos - 1; i >= 0; i -= 1) {
      const char = text[i]
      if (char === '@') {
        atPos = i
        break
      }
      if (char === ' ' || char === '\n' || char === '\r') break
    }

    if (atPos >= 0 && (atPos === 0 || /\s/.test(text[atPos - 1] ?? ''))) {
      anchorRef.current = { node: textNode, offset: atPos }
      setFilterText(text.substring(atPos + 1, cursorPos))
      setShowDropdown(true)
      setHighlightIndex(0)
      return
    }

    closeDropdown()
  }, [candidates.length, closeDropdown])

  const removeChip = useCallback(
    (chip: HTMLElement) => {
      chip.remove()
      const editor = editorRef.current
      if (editor && editor.textContent === '') {
        editor.innerHTML = ''
      }
      emitContentState()
      adjustHeight()
      if (editor) {
        placeCaretAtEnd(editor)
      }
    },
    [adjustHeight, emitContentState],
  )

  const insertMention = useCallback(
    (candidate: MentionCandidate) => {
      const editor = editorRef.current
      const anchor = anchorRef.current
      if (!editor || !anchor) {
        closeDropdown()
        return
      }

      const anchorNode = anchor.node
      if (anchorNode.nodeType !== Node.TEXT_NODE || !anchorNode.parentNode) {
        closeDropdown()
        return
      }

      const fullText = anchorNode.textContent ?? ''
      const mentionLength = 1 + filterText.length
      const before = fullText.substring(0, anchor.offset)
      const after = fullText.substring(anchor.offset + mentionLength)
      const parent = anchorNode.parentNode

      const beforeNode = document.createTextNode(before)
      const chip = createMentionChipElement(candidate, projectId)
      const trailingSpacer = after.length === 0 || /^\s/.test(after) ? '' : ' '
      const afterNode = document.createTextNode(`${trailingSpacer}${after}`)

      parent.insertBefore(beforeNode, anchorNode)
      parent.insertBefore(chip, anchorNode)
      parent.insertBefore(afterNode, anchorNode)
      parent.removeChild(anchorNode)

      const selection = window.getSelection()
      if (selection) {
        const range = document.createRange()
        range.setStart(afterNode, trailingSpacer.length)
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
      }

      emitContentState()
      adjustHeight()
      closeDropdown()
      editor.focus()
    },
    [adjustHeight, closeDropdown, emitContentState, filterText, projectId],
  )

  const handleInput = useCallback(() => {
    if (composingRef.current) return
    emitContentState()
    adjustHeight()
    syncMentionTrigger()
  }, [adjustHeight, emitContentState, syncMentionTrigger])

  const handleCompositionStart = useCallback(() => {
    composingRef.current = true
  }, [])

  const handleCompositionEnd = useCallback(() => {
    composingRef.current = false
    handleInput()
  }, [handleInput])

  const insertTextAtCursor = useCallback((value: string) => {
    const editor = editorRef.current
    if (!editor) return

    const selection = window.getSelection()
    if (!selectionInsideEditor(editor, selection)) {
      placeCaretAtEnd(editor)
    }

    const activeSelection = window.getSelection()
    if (!activeSelection || activeSelection.rangeCount === 0) return

    const range = activeSelection.getRangeAt(0)
    range.deleteContents()

    const textNode = document.createTextNode(value)
    range.insertNode(textNode)
    range.setStart(textNode, value.length)
    range.collapse(true)
    activeSelection.removeAllRanges()
    activeSelection.addRange(range)
    editor.focus()
  }, [])

  const removeAdjacentChip = useCallback(
    (direction: 'backward' | 'forward') => {
      const editor = editorRef.current
      if (!editor) return false

      const selection = window.getSelection()
      if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
        return false
      }

      const range = selection.getRangeAt(0)
      if (!range.collapsed) return false

      let adjacentNode: globalThis.Node | null = null
      if (range.startContainer.nodeType === Node.TEXT_NODE) {
        const textNode = range.startContainer as Text
        if (direction === 'backward' && range.startOffset === 0) {
          adjacentNode = textNode.previousSibling
        } else if (
          direction === 'forward' &&
          range.startOffset === (textNode.textContent ?? '').length
        ) {
          adjacentNode = textNode.nextSibling
        }
      } else if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
        const container = range.startContainer as HTMLElement
        const childIndex =
          direction === 'backward' ? range.startOffset - 1 : range.startOffset
        adjacentNode = container.childNodes[childIndex] ?? null
      }

      if (
        adjacentNode instanceof HTMLElement &&
        adjacentNode.dataset.mentionPill === 'true'
      ) {
        removeChip(adjacentNode)
        return true
      }

      return false
    },
    [removeChip],
  )

  // Serialize selection for copy/cut
  const serializeSelection = useCallback((): string | null => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null

    const range = selection.getRangeAt(0)
    const fragment = range.cloneContents()
    const container = document.createElement('div')
    container.appendChild(fragment)

    const serialized = serializeNodeText(container, container)
    return serialized || null
  }, [])

  const handleCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const serialized = serializeSelection()
    if (!serialized) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', serialized)
  }, [serializeSelection])

  const handleCut = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const serialized = serializeSelection()
    if (!serialized) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', serialized)

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      selection.getRangeAt(0).deleteContents()
    }

    const editor = editorRef.current
    if (editor && editor.textContent === '') {
      editor.innerHTML = ''
    }
    emitContentState()
    adjustHeight()
  }, [adjustHeight, emitContentState, serializeSelection])

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()

    const editor = editorRef.current
    if (!editor) return

    const text = e.clipboardData.getData('text/plain')
    if (!text) return

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    if (!range.collapsed) {
      range.deleteContents()
    }

    const MARKER_RE = /@\[\[([^\]]+)\]\]/g
    const hasMarkers = MARKER_RE.test(text)

    if (hasMarkers) {
      MARKER_RE.lastIndex = 0
      const frag = document.createDocumentFragment()
      let lastIndex = 0
      let match: RegExpExecArray | null

      while ((match = MARKER_RE.exec(text)) !== null) {
        if (match.index > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
        }
        const nodeId = match[1]
        const candidate = candidates.find((c) => c.id === nodeId)
        if (candidate) {
          frag.appendChild(createMentionChipElement(candidate, projectId))
        } else {
          frag.appendChild(document.createTextNode(match[0]))
        }
        lastIndex = match.index + match[0].length
      }
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)))
      }

      const lastChild = frag.lastChild
      range.insertNode(frag)

      if (lastChild) {
        range.setStartAfter(lastChild)
        range.collapse(true)
      } else {
        range.collapse(false)
      }
      selection.removeAllRanges()
      selection.addRange(range)
    } else {
      const textNode = document.createTextNode(text)
      range.insertNode(textNode)
      range.setStartAfter(textNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    emitContentState()
    adjustHeight()
  }, [adjustHeight, candidates, emitContentState, projectId])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // IME composition guard
      if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return

      if (showDropdown && filteredCandidates.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          setHighlightIndex((prev) => {
            const next = (prev + 1) % filteredCandidates.length
            onMentionClick?.(filteredCandidates[next].id)
            return next
          })
          return
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          setHighlightIndex((prev) => {
            const next = (prev - 1 + filteredCandidates.length) % filteredCandidates.length
            onMentionClick?.(filteredCandidates[next].id)
            return next
          })
          return
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault()
          insertMention(filteredCandidates[highlightIndex])
          return
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          closeDropdown()
          return
        }
      }

      if (event.key === 'Backspace' && removeAdjacentChip('backward')) {
        event.preventDefault()
        return
      }
      if (event.key === 'Delete' && removeAdjacentChip('forward')) {
        event.preventDefault()
        return
      }

      if (event.key === 'Enter' && !event.shiftKey && !showDropdown) {
        event.preventDefault()
        onSubmit?.()
      }

      if (event.key === 'Escape' && !showDropdown) {
        event.preventDefault()
        onEscape?.()
      }
    },
    [closeDropdown, filteredCandidates, highlightIndex, insertMention, onEscape, onMentionClick, onSubmit, removeAdjacentChip, showDropdown],
  )

  const handleEditorClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return

      const removeButton = target.closest('[data-mention-chip-remove]') as HTMLElement | null
      if (removeButton) {
        event.preventDefault()
        event.stopPropagation()
        const chip = removeButton.closest('[data-mention-pill]') as HTMLElement | null
        if (chip) {
          removeChip(chip)
        }
        return
      }

      const chip = target.closest('[data-mention-pill]') as HTMLElement | null
      if (chip?.dataset.nodeId) {
        event.preventDefault()
        event.stopPropagation()
        onMentionClick?.(chip.dataset.nodeId)
      }
    },
    [onMentionClick, removeChip],
  )

  useImperativeHandle(
    ref,
    () => ({
      clear: () => {
        const editor = editorRef.current
        if (!editor) return
        editor.innerHTML = ''
        closeDropdown()
        emitContentState()
        adjustHeight()
      },
      focus: () => {
        const editor = editorRef.current
        if (!editor) return
        placeCaretAtEnd(editor)
      },
      setText: (text: string) => {
        const editor = editorRef.current
        if (!editor) return
        hydrateContent(editor, text, candidates, projectId)
        emitContentState()
        adjustHeight()
        placeCaretAtEnd(editor)
      },
      insertMentionTrigger: () => {
        const editor = editorRef.current
        if (!editor || disabled) return

        const selection = window.getSelection()
        if (!selectionInsideEditor(editor, selection)) {
          placeCaretAtEnd(editor)
        }

        const activeSelection = window.getSelection()
        const range = activeSelection?.rangeCount ? activeSelection.getRangeAt(0) : null
        const beforeText = range
          ? (() => {
              const clone = range.cloneRange()
              clone.selectNodeContents(editor)
              clone.setEnd(range.startContainer, range.startOffset)
              return clone.toString().replace(/\u00A0/g, ' ')
            })()
          : ''
        const needsSpace = beforeText.length > 0 && !/\s$/.test(beforeText)

        insertTextAtCursor(needsSpace ? ' @' : '@')
        handleInput()
      },
      getPlainText: () => getPlainTextFromEditor(editorRef.current),
      getReferencedNodeIds: () => getReferencedNodeIdsFromEditor(editorRef.current),
    }),
    [adjustHeight, candidates, closeDropdown, disabled, emitContentState, handleInput, insertTextAtCursor, projectId],
  )

  // Hydrate initialContent: parse @[[nodeId]] markers into mention chips.
  const initialContentHydratedRef = useRef(false)
  useEffect(() => {
    const editor = editorRef.current
    if (!editor || !initialContent || initialContentHydratedRef.current) return
    const hasMarkers = /@\[\[[^\]]+\]\]/.test(initialContent)
    if (hasMarkers && canvasNodes === undefined) return
    initialContentHydratedRef.current = true

    hydrateContent(editor, initialContent, candidates, projectId)
    emitContentState()
    adjustHeight()
    placeCaretAtEnd(editor)
  }, [initialContent, canvasNodes, candidates, emitContentState, adjustHeight, projectId])

  useEffect(() => {
    emitContentState()
    adjustHeight()
  }, [adjustHeight, emitContentState])

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const editor = editorRef.current
      const dropdown = dropdownRef.current
      const target = event.target as globalThis.Node | null
      if (!editor || !target) return
      if (!editor.contains(target) && !(dropdown && dropdown.contains(target)) && !(target as HTMLElement).closest?.('[data-testid="mention-dropdown"]')) {
        closeDropdown()
      }
    }

    const editor = editorRef.current
    if (!editor) return

    editor.addEventListener('click', handleEditorClick)
    document.addEventListener('mousedown', handleDocumentClick)
    return () => {
      editor.removeEventListener('click', handleEditorClick)
      document.removeEventListener('mousedown', handleDocumentClick)
    }
  }, [closeDropdown, handleEditorClick])

  // Scroll highlighted item into view
  useEffect(() => {
    if (!showDropdown || !dropdownRef.current) return
    const items = dropdownRef.current.querySelectorAll('[data-mention-item]')
    items[highlightIndex]?.scrollIntoView({ block: 'nearest' })
  }, [highlightIndex, showDropdown])

  return (
    <div className="relative">
      {showDropdown && (
        <MentionDropdownPortal
          candidates={filteredCandidates}
          highlightIndex={highlightIndex}
          projectId={projectId}
          editorRef={editorRef}
          dropdownRef={dropdownRef}
          onSelect={insertMention}
          onHighlight={setHighlightIndex}
        />
      )}

      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        data-testid="chat-composer-editor"
        data-placeholder={placeholder}
        className={`inline-mention-composer__editor w-full overflow-y-auto whitespace-pre-wrap break-words bg-transparent px-4 pt-3 pb-2 text-sm text-foreground focus:outline-none ${
          disabled ? 'cursor-not-allowed opacity-50' : ''
        }`}
        style={{ minHeight: '24px', maxHeight: '160px' }}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onCopy={handleCopy}
        onCut={handleCut}
        onPaste={handlePaste}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onFocus={() => onFocusChange?.(true)}
        onBlur={() => onFocusChange?.(false)}
      />

      <style>{`
        .inline-mention-composer__editor:empty::before {
          content: attr(data-placeholder);
          color: var(--text-muted, #6a6a8a);
          pointer-events: none;
        }
      `}</style>
    </div>
  )
})
